// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Drawer, type DrawerProps } from '@mui/material';
import { isUndefined } from '@shared/utils';
import {
  type ComponentType,
  type ReactElement,
  useCallback,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';

/**
 * Props passed to the optional Trigger component that opens the drawer. Use a
 * button or any clickable element that receives these props.
 */
export type DrawerTriggerProps = {
  /** Called when the trigger is clicked; opens the drawer. */
  onClick?: () => void;
  /** When true, the trigger should be disabled (e.g. not open the drawer). */
  disabled?: boolean;
};

/** Options to configure the drawer hook behavior and lifecycle callbacks. */
export interface DrawerOptions {
  /** Whether the drawer is open on first render. Defaults to `false`. */
  defaultOpen?: boolean;
  /** Called after the drawer has closed. */
  afterClose?: () => void;
  /** Called before the drawer starts closing. */
  beforeClose?: () => void;
  /** Called before the drawer starts opening. */
  beforeOpen?: () => void;
  /** Called after the drawer has opened. */
  afterOpen?: () => void;
  /**
   * Controlled open state. When provided together with `onClose`, the drawer uses
   * this state instead of internal state (controlled mode).
   */
  open?: boolean;
  /** Controlled close handler. Use with `open` for controlled mode. */
  onClose?: () => void;
  /**
   * Controlled open handler. Use with `open` for controlled mode (e.g. for
   * Trigger).
   */
  onOpen?: () => void;
}

/** Default options for {@link useDrawer}. */
export const DEFAULT_DRAWER_OPTIONS = {
  defaultOpen: false,
} satisfies DrawerOptions;

/**
 * Base hook for a temporary MUI Drawer with optional trigger element.
 *
 * Manages open/close state and exposes a `Renderer` component that renders an
 * optional Trigger (e.g. button) and the Drawer. The Drawer uses
 * `variant="temporary"` by default and can be customized via standard
 * {@link https://v5.mui.com/material-ui/react-drawer/#temporary-drawer | MUI Drawer props}
 * (e.g. `anchor`, `PaperProps`, `ModalProps`).
 *
 * @example With a button as trigger
 *
 * ```tsx
 * const [DrawerRenderer, { open, onOpen, onClose }] = useDrawer();
 *
 * <DrawerRenderer
 *   Trigger={({ onClick }) => <Button onClick={onClick}>Open</Button>}
 *   anchor="right"
 *   PaperProps={{ sx: { width: 320 } }}
 * >
 *   <Box p={2}>Drawer content</Box>
 * </DrawerRenderer>;
 * ```
 *
 * @example Without trigger (manual control only)
 *
 * ```tsx
 * const [DrawerRenderer, { onOpen, onClose }] = useDrawer();
 *
 * <DrawerRenderer anchor="right">
 *   <Box p={2}>Content</Box>
 * </DrawerRenderer>
 * <Button onClick={onOpen}>Open drawer</Button>
 * ```
 *
 * @param options - Configuration and lifecycle callbacks. Defaults to
 *   {@link DEFAULT_DRAWER_OPTIONS}.
 * @returns A tuple `[Renderer, controls]`:
 *
 *   - `Renderer`: Component that accepts optional `Trigger`, `triggerDisabled`, and any
 *       MUI `DrawerProps` (except `open`/`onClose`).
 *   - `controls`: `{ open, onClose, onOpen }` for imperative control.
 *
 * @see {@link https://v5.mui.com/material-ui/react-drawer/#temporary-drawer | MUI Drawer - Temporary drawer}
 */
// istanbul ignore next
export const useDrawer = ({
  defaultOpen = false,
  afterClose,
  beforeClose,
  beforeOpen,
  afterOpen,
  open: controlledOpen,
  onClose: controlledOnClose,
  onOpen: controlledOnOpen,
}: DrawerOptions = DEFAULT_DRAWER_OPTIONS) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled =
    !isUndefined(controlledOpen) && !isUndefined(controlledOnClose);
  const open = isControlled ? controlledOpen : internalOpen;
  const openRef = useRef(open);
  openRef.current = open;

  const onClose = useCallback(() => {
    beforeClose?.();
    if (isControlled) {
      controlledOnClose?.();
    } else {
      setInternalOpen(false);
    }
    afterClose?.();
  }, [afterClose, beforeClose, isControlled, controlledOnClose]);

  const onOpen = useCallback(() => {
    beforeOpen?.();
    if (isControlled) {
      controlledOnOpen?.();
    } else {
      setInternalOpen(true);
    }
    afterOpen?.();
  }, [afterOpen, beforeOpen, isControlled, controlledOnOpen]);

  const onCloseRef = useRef(onClose);
  const onOpenRef = useRef(onOpen);
  onCloseRef.current = onClose;
  onOpenRef.current = onOpen;

  const onDrawerClose = useCallback(() => onCloseRef.current(), []);
  const onDrawerOpen = useCallback(() => onOpenRef.current(), []);

  const Renderer = useCallback(
    ({
      Trigger,
      triggerDisabled,
      ...drawerProps
    }: PropsWithChildren<
      Omit<DrawerProps, 'open' | 'onClose'> & {
        Trigger?: ComponentType<DrawerTriggerProps>;
        triggerDisabled?: boolean;
      }
    >) => (
      <>
        {Trigger && <Trigger onClick={onDrawerOpen} disabled={triggerDisabled} />}
        <Drawer
          open={openRef.current}
          onClose={onDrawerClose}
          variant="temporary"
          {...drawerProps}
        >
          {drawerProps?.children as ReactElement}
        </Drawer>
      </>
    ),
    [onDrawerClose, onDrawerOpen],
  );

  return [
    Renderer,
    {
      open,
      onClose,
      onOpen,
    },
  ] as const;
};
